import AddCircleIcon from "@mui/icons-material/AddCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { Box, Chip, IconButton, InputAdornment, SxProps, TextField, Theme, Typography, styled } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { roundMacros, scaleMacros } from "@notemeal/shared-utils-macro-protocol";
import { useEnvironmentType } from "apps/web/src/contexts/useEnvType";
import classnames from "classnames";
import { ReactNode, useEffect, useState } from "react";
import { animated } from "react-spring";
import { ReactEventHandlers } from "react-use-gesture/dist/types";
import { DraggableSpring } from "shared-ui/hooks/useDraggable";
import { MacrosSummaryLabel } from "shared-ui/Macros/SummaryLabel";
import { ServingAmountsIngredientsList } from "shared-ui/ServingAmount/IngredientsList";
import { ServingAmountLimitedAccessWarning } from "shared-ui/ServingAmount/LimitedAccessWarning";
import { ServingAmountTooltip } from "shared-ui/ServingAmount/Tooltip";
import { addWeightToAmbiguousServingString } from "shared-ui/ServingAmount/utils";
import { useAthleteHighlightFromServingAmount } from "shared-ui/utils/Foods/foods";
import { inputToNumber } from "shared-ui/utils/inputUtils";
import { FullServingAmountFragment, FullServingFragment } from "../../../types";
import { useIsFoodFormRedesign } from "../../FoodFormUi/FoodFormUiContext";
import { IngredientSourceChip } from "../IngredientSourceChip";
import { getIngredientSourceChipFromFoodOrRecipe } from "../ingredientSource";
import ServingAmountEditAmount from "./Amount";
import ServingsMenu from "./ServingsMenu";

const LabelRow = styled(Box)(() => ({ display: "flex", flexDirection: "row", alignItems: "center" }));
const ChipLabelWrapper = styled(Box)(() => ({ overflow: "hidden", textOverflow: "ellipsis" }));

const useStyles = makeStyles(({ spacing, palette: { like, dislike, primary } }: Theme) =>
  createStyles({
    chipLimitedAccess: {
      height: spacing(7),
    },
    chipDefault: {
      height: spacing(4),
    },
    chip: {
      marginTop: spacing(),
      display: "flex",
      flexGrow: 0,
      flexShrink: 0,
      flexWrap: "nowrap",
      borderRadius: spacing(1),
    },
    marginBottom: {
      marginBottom: spacing(),
    },
    greenChip: {
      backgroundColor: like.lighter,
      "&:hover": {
        backgroundColor: like.light,
      },
      "&:focus": {
        backgroundColor: like.light,
      },
    },
    redChip: {
      backgroundColor: dislike.lighter,
      "&:hover": {
        backgroundColor: dislike.light,
      },
      "&:focus": {
        backgroundColor: dislike.light,
      },
    },
    chipLabel: {
      flexGrow: 1,
      flexShrink: 1,
      minWidth: 0,
    },
    foodName: {
      lineHeight: 1.2,
      fontWeight: "bold",
    },
    notExpanded: { borderRadius: spacing(1) },
    expanded: { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
    focusVisible: {
      "&:focus-visible": {
        outline: "2px solid",
        outlineColor: primary.main,
        outlineOffset: "2px",
        borderRadius: "50%",
      },
    },
  })
);

export interface RenderServingsMenuArgs {
  anchorEl: HTMLElement;
  onClose: () => void;
  servingAmount: FullServingAmountFragment;
}

interface ServingAmountsEditChipProps {
  servingAmount: FullServingAmountFragment;
  onIncrement: () => void;
  onDecrement: () => void;
  onDelete: () => void;
  onReplaceServing: (serving: FullServingFragment, amount?: number) => void;
  onSetAmount: (amount: number) => void;
  expanded: boolean;
  onChangeExpanded: (expanded: boolean) => void;
  bindProps?: ReactEventHandlers;
  spring?: DraggableSpring;
  servingsMenuDisabled?: boolean;
  className?: string;
  recipeIngredient: boolean;
  onDeconstructRecipe?: () => void;
  onLoadIngredients: (ingredientsCount: number) => void;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  displayMacros?: boolean;
}

const ServingAmountsEditChip = ({
  servingAmount,
  onIncrement,
  onDecrement,
  onDelete,
  onSetAmount,
  onReplaceServing,
  expanded,
  onChangeExpanded,
  bindProps,
  spring,
  recipeIngredient,
  servingsMenuDisabled: _servingMenuDisabled,
  className,
  onDeconstructRecipe,
  onLoadIngredients,
  sx,
  disabled,
  displayMacros = true,
}: ServingAmountsEditChipProps) => {
  const classes = useStyles();
  const isRedesign = useIsFoodFormRedesign();
  const { isCommercial } = useEnvironmentType();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { highlightType: highlightColor } = useAthleteHighlightFromServingAmount(servingAmount.serving.foodOrRecipe);

  const { serving, amount } = servingAmount;
  const foodOrRecipe = serving.foodOrRecipe;
  const sourceChip = isRedesign ? getIngredientSourceChipFromFoodOrRecipe(foodOrRecipe) : null;
  const servingsMenuDisabled = disabled || _servingMenuDisabled || !foodOrRecipe.hasFullAccess;
  const [amountInput, setAmountInput] = useState(() => (amount ? String(amount) : ""));
  const [amountInputFocused, setAmountInputFocused] = useState(false);

  useEffect(() => {
    if (!amountInputFocused) {
      setAmountInput(amount ? String(amount) : "");
    }
  }, [amount, amountInputFocused]);

  const chipClassName = classnames([
    classes.chip,
    className,
    ...(foodOrRecipe.__typename === "Recipe" ? [] : [classes.marginBottom]),
    {
      [classes.redChip]: highlightColor === "Red",
      [classes.greenChip]: highlightColor === "Green",
      [classes.expanded]: expanded,
      [classes.notExpanded]: !expanded,
      [classes.chipDefault]: foodOrRecipe.hasFullAccess,
      [classes.chipLimitedAccess]: !foodOrRecipe.hasFullAccess,
    },
  ]);

  let style: any = {};
  if (spring) {
    const { zIndex, y, shadow } = spring;
    style = {
      zIndex,
      boxShadow: shadow.to(s => `rgba(0, 0, 0, 0.15) 0px ${s}px ${2 * s}px 0px`),
      transform: y.to(y => `translate3d(0, ${y}px, 0)`),
    };
  }

  if (isRedesign) {
    const scaledMacros = roundMacros(scaleMacros(serving.macros, amount));
    const inputRootSx = {
      height: 28,
      fontSize: 12,
      lineHeight: "16px",
      borderRadius: "6px",
      bgcolor: "common.white",
    };

    return (
      <>
        <Box
          component={animated.div}
          style={style}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: 1,
            p: 1,
            borderRadius: "6px",
            bgcolor: "greyscale.100",
            ...sx,
          }}
        >
          {bindProps && !disabled && (
            <DragIndicatorIcon {...bindProps} sx={{ fontSize: 24, color: "greyscale.500", cursor: "grab", flexShrink: 0 }} />
          )}
          <Box sx={{ minWidth: 0, flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: "20px",
                  color: "text.primary",
                  m: 0,
                  minWidth: 0,
                }}
              >
                {foodOrRecipe.name}
              </Typography>
              {sourceChip && <IngredientSourceChip chip={sourceChip} />}
              {!foodOrRecipe.hasFullAccess && <ServingAmountLimitedAccessWarning serving={serving} />}
            </Box>
            {displayMacros && (
              <MacrosSummaryLabel
                macros={scaledMacros}
                kcalPrefix
                variant="xs"
                sx={{ fontSize: 12, lineHeight: "16px", m: 0, display: "block" }}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <TextField
              disabled={disabled}
              value={amountInput}
              type="number"
              size="small"
              margin="none"
              onFocus={() => setAmountInputFocused(true)}
              onBlur={() => setAmountInputFocused(false)}
              onChange={event => {
                const next = event.target.value;
                setAmountInput(next);
                const parsed = inputToNumber(next);
                if (parsed !== null && parsed >= 0.125) {
                  onSetAmount(parsed);
                }
              }}
              inputProps={{
                "aria-label": "Units count",
                step: 0.25,
                min: 0.125,
                sx: {
                  py: 0,
                  px: 0,
                  textAlign: "center",
                  fontSize: 12,
                  lineHeight: "16px",
                  height: "100%",
                  boxSizing: "border-box",
                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  MozAppearance: "textfield",
                },
              }}
              InputProps={{
                sx: {
                  ...inputRootSx,
                  px: 2,
                  minHeight: 28,
                  boxSizing: "border-box",
                },
              }}
              sx={{
                width: 60,
                m: 0,
              }}
            />
            <TextField
              disabled={servingsMenuDisabled}
              value={addWeightToAmbiguousServingString(serving.units, serving.weight ? serving.weight * amount : null)}
              size="small"
              margin="none"
              onClick={event => {
                event.stopPropagation();
                if (!servingsMenuDisabled) {
                  setAnchorEl(event.currentTarget);
                }
              }}
              onKeyDown={event => {
                if (servingsMenuDisabled) {
                  return;
                }
                if (event.key === "Enter" || event.key === " ") {
                  if (event.key === " ") {
                    event.preventDefault();
                  }
                  setAnchorEl(event.currentTarget);
                }
              }}
              InputProps={{
                readOnly: true,
                sx: {
                  ...inputRootSx,
                  pl: 2,
                  pr: 0.5,
                  minHeight: 28,
                  boxSizing: "border-box",
                },
                endAdornment: (
                  <InputAdornment position="end" sx={{ m: 0 }}>
                    <KeyboardArrowDownIcon sx={{ fontSize: 20, color: "action.active", pointerEvents: "none" }} />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                sx: {
                  py: 0,
                  px: 0,
                  fontSize: 12,
                  lineHeight: "16px",
                  height: "100%",
                  boxSizing: "border-box",
                },
              }}
              sx={{
                width: 140,
                m: 0,
              }}
            />
          </Box>
          {!disabled && (
            <IconButton onClick={onDelete} aria-label="Remove" sx={{ p: 0, width: 20, height: 20, flexShrink: 0 }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>
        {anchorEl && (
          <ServingsMenu
            foodOrRecipeId={servingAmount.serving.foodOrRecipe.id}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            includeRecipeOnlyIngredients={recipeIngredient}
            onSelect={(nextServing, nextAmount) => onReplaceServing(nextServing, nextAmount)}
            allowServingRequest={isCommercial && servingAmount.serving.foodOrRecipe.__typename === "GenericFood"}
          />
        )}
      </>
    );
  }

  const editAmountComponent = (
    <ServingAmountEditAmount
      disabled={disabled}
      onSetAmount={onSetAmount}
      amount={servingAmount.amount}
      serving={serving}
      onClick={e => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
      }}
      ariaLabel="Units count"
      servingsMenuDisabled={servingsMenuDisabled}
    />
  );

  const ingredientsComponent = expanded && (
    <ServingAmountsIngredientsList
      recipeId={foodOrRecipe.id}
      hasOwnNutrients={serving.hasOwnNutrients}
      perRecipeYield={serving.perRecipeYield || 1}
      units={serving.units}
      style={style}
      onDeconstruct={onDeconstructRecipe}
      onLoad={onLoadIngredients}
      onClose={() => onChangeExpanded(!expanded)}
    />
  );

  return (
    <>
      <Chip
        sx={{ ...sx, backgroundColor: "greyscale.100" }}
        className={chipClassName}
        classes={{ label: classes.chipLabel }}
        component={animated.div}
        style={style}
        icon={
          disabled ? undefined : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {bindProps && <DragIndicatorIcon {...bindProps} />}
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  onDecrement();
                }}
                disabled={amount <= 0.125}
                disableRipple
                aria-label="Remove unit"
                size="large"
                sx={{
                  m: 0,
                  p: 0,
                  color: servingAmount.amount <= 0.125 ? "disabled" : "common.black",
                }}
                className={classes.focusVisible}
              >
                <RemoveCircleIcon />
              </IconButton>
              <IconButton
                sx={{ m: 0, p: 0, color: "common.black" }}
                className={classes.focusVisible}
                onClick={e => {
                  e.stopPropagation();
                  onIncrement();
                }}
                aria-label="Add unit"
                disableRipple
                size="large"
              >
                <AddCircleIcon />
              </IconButton>
              <IconButton
                sx={{ m: 0, p: 0, color: "common.black" }}
                className={classes.focusVisible}
                onClick={e => {
                  e.stopPropagation();
                  onDelete();
                }}
                aria-label={`Remove ${foodOrRecipe.name}`}
                disableRipple
                size="large"
              >
                <CancelIcon />
              </IconButton>
            </Box>
          )
        }
        label={
          <Label
            serving={serving}
            amount={amount}
            recipeIngredient={recipeIngredient}
            expanded={expanded}
            onChangeExpanded={onChangeExpanded}
            editAmountComponent={editAmountComponent}
            servingsMenuDisabled={servingsMenuDisabled}
            setAnchorEl={setAnchorEl}
          />
        }
      />
      {ingredientsComponent}
      {anchorEl && (
        <ServingsMenu
          foodOrRecipeId={servingAmount.serving.foodOrRecipe.id}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          includeRecipeOnlyIngredients={recipeIngredient}
          onSelect={(serving, amount) => onReplaceServing(serving, amount)}
          allowServingRequest={isCommercial && servingAmount.serving.foodOrRecipe.__typename === "GenericFood"}
        />
      )}
    </>
  );
};

export default ServingAmountsEditChip;

interface FullLabelProps {
  serving: FullServingFragment;
  amount: number;
  recipeIngredient: boolean;
  expanded: boolean;
  servingsMenuDisabled: boolean;
  onChangeExpanded: (expanded: boolean) => void;
  setAnchorEl: (el: HTMLElement | null) => void;
  editAmountComponent: ReactNode;
}

const Label = ({
  serving,
  onChangeExpanded,
  setAnchorEl,
  amount,
  editAmountComponent,
  expanded,
  recipeIngredient,
  servingsMenuDisabled,
}: FullLabelProps) => {
  const classes = useStyles();
  const { foodOrRecipe, macros } = serving;
  const { name, __typename, hasFullAccess } = foodOrRecipe;

  if (hasFullAccess) {
    const isExpandable = __typename === "Recipe";
    return (
      <ServingAmountTooltip serving={serving} amount={amount}>
        <ChipLabelWrapper
          onClick={e => {
            if (isExpandable) {
              onChangeExpanded(!expanded);
            } else if (!recipeIngredient && !servingsMenuDisabled) {
              setAnchorEl(e.currentTarget);
            }
          }}
        >
          <LabelRow>
            <Typography variant="subtitle1" classes={{ root: classes.foodName }} noWrap>
              {name}
            </Typography>
            {isExpandable && (expanded ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />)}
          </LabelRow>
          {editAmountComponent}
        </ChipLabelWrapper>
      </ServingAmountTooltip>
    );
  } else {
    const scaledMacros = roundMacros(scaleMacros(macros, amount));

    return (
      <ChipLabelWrapper
        onClick={e => {
          if (!recipeIngredient && !servingsMenuDisabled) {
            setAnchorEl(e.currentTarget);
          }
        }}
      >
        <LabelRow>
          <Typography variant="subtitle1" classes={{ root: classes.foodName }} noWrap>
            {name}
          </Typography>
          <ServingAmountLimitedAccessWarning serving={serving} />
        </LabelRow>
        {editAmountComponent}
        <MacrosSummaryLabel macros={scaledMacros} kcalPrefix variant="xs" />
      </ChipLabelWrapper>
    );
  }
};
